import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/icons';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

const HomeCarePage: React.FC = () => {
  // Patient personas data
  const patientPersonas = [
    {
      id: 1,
      title: "Chronic Patient",
      name: "Mrs. Zhang",
      age: 65,
      condition: "Hypertension, Diabetes",
      icon: "MonitorHeart",
      challenge: "Needs daily monitoring of blood pressure and glucose. Handwritten records are easily lost and trends are hard to track.",
      solution: "Uses mobile photo scanning for device readings. AI automatically records and generates trend charts with timely alerts.",
      benefit: "Better control of health metrics. Doctors see complete, accurate records during follow-ups.",
      color: "blue"
    },
    {
      id: 2,
      title: "Alzheimer's Care",
      name: "Mr. Li",
      age: 78,
      condition: "Early-stage Dementia",
      icon: "Psychology",
      challenge: "Memory decline leads to missed medication. Family members worry when they are not around.",
      solution: "Smart medication reminders with audible alerts and voice prompts on exactly what to take.",
      benefit: "No more missed doses. Condition stays stable, giving the family peace of mind.",
      color: "indigo"
    },
    {
      id: 3,
      title: "Post-Surgery Recovery",
      name: "Ms. Chen",
      age: 45,
      condition: "Post-Knee Replacement",
      icon: "FitnessCenter",
      challenge: "Uncertain about recovery progress. Worries about over-exertion or insufficient exercise.",
      solution: "Phase-based exercise reminders and progress tracking. Early warning for abnormal recovery signs.",
      benefit: "Excellent recovery progress. Restored walking ability two weeks earlier than expected.",
      color: "teal"
    }
  ];

  // Core features data
  const coreFeatures = [
    {
      id: 1,
      title: "Smart Document Scanning",
      icon: "DocumentScanner",
      benefit: "Snap a photo, AI does the rest",
      description: "Say goodbye to manual logging. AI automatically extracts key values to build your personal health profile.",
      features: [
        "Auto-classify prescriptions & reports",
        "Digitize BP & Glucose monitor readings",
        "High-light abnormal results instantly",
        "Compare current data with history"
      ],
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      id: 2,
      title: "24/7 Health Monitoring",
      icon: "MonitorHeart",
      benefit: "Detect anomalies early",
      description: "Let AI help you understand complex medical data. Generate easy-to-read reports and stay in control.",
      features: [
        "Daily vital signs trend tracking",
        "Personalized risk score warnings",
        "One-click doctor's visit summary",
        "Authorized family member access"
      ],
      gradient: "from-indigo-500 to-purple-400"
    },
    {
      id: 3,
      title: "Concierge Medication Alerts",
      icon: "Medication",
      benefit: "Never miss a dose again",
      description: "Smart medication safety guard. Auto-setup alerts based on prescriptions and check for drug interactions.",
      features: [
        "Personalized voice reminders",
        "Real-time drug-drug interaction check",
        "Low pill count smart notifications",
        "Detailed adherence track records"
      ],
      gradient: "from-emerald-500 to-teal-400"
    }
  ];

  // Use cases with conversational UI
  const [activeUseCase, setActiveUseCase] = useState(0);
  
  const useCases = [
    {
      id: 1,
      title: "Personalized Workout",
      icon: "FitnessCenter",
      scenario: "Wants to start exercising but worries about risks",
      messages: [
        {
          type: "user",
          content: "I have diabetes and hypertension and want to start exercising. Can you help me create a safe plan?"
        },
        {
          type: "ai",
          content: "Based on your health profile (Diabetes & Hypertension), I recommend a gradual approach:\n\n• **Aerobic**: Walk for 30 mins daily, keeping heart rate between 100-120 bpm.\n• **Flexibility**: Tai Chi or basic Yoga 3x weekly to improve circulation.\n• **Safety**: Avoid breath-holding weight exercises. Check BP before starting.\n\n💡 Suggestion: Snap a photo of your latest lab report, and I can fine-tune your target heart rate zones."
        }
      ]
    },
    {
      id: 2,
      title: "Dietary Consultation",
      icon: "Restaurant",
      scenario: "Feeling unwell after meals, seeking potential causes",
      messages: [
        {
          type: "user",
          content: "I've been feeling bloated after eating lately. What could be the reason?"
        },
        {
          type: "ai",
          content: "Analyzing your recent history and symptoms:\n\n1. **Dietary Link**: Your dinner yesterday included high-fiber foods; eating too quickly may cause bloating.\n2. **Medication**: Your Metformin can occasionally cause GI side effects. Try taking it with meals.\n3. **Advice**: Try smaller, more frequent meals. If symptoms persist for 3 days, I've prepared a summary for your doctor.\n\n💡 Would you like me to set a follow-up check-in 2 hours after your next meal?"
        }
      ]
    },
    {
      id: 3,
      title: "Supplement Safety",
      icon: "LocalPharmacy",
      scenario: "Considering supplements, checking for safety",
      messages: [
        {
          type: "user",
          content: "A friend recommended Omega-3 for heart health. I'm on Aspirin, is it safe to take?"
        },
        {
          type: "ai",
          content: "⚠️ **Important Safety Risk Alert**\n\nChecked your current medication: **Aspirin** (Antiplatelet).\n\n**Risk Analysis**: Omega-3 (Fish Oil) also has natural blood-thinning effects. Combining them can **significantly increase bleeding risk** (e.g., bruising or gum bleeding).\n\n**Recommendation**:\n• **Stop** taking Fish Oil until you consult your primary doctor.\n• If your doctor approves, start with a very low dose.\n\nYour safety is our priority. 💡 Need help finding your cardiologist's contact info?"
        }
      ]
    }
  ];

  // How it works steps
  const steps = [
    {
      number: "01",
      title: "Quick Start",
      description: "2-minute registration. AI guides you to sync your health status without tedious manual entry.",
      icon: "PersonAdd"
    },
    {
      number: "02",
      title: "Snap & Sync",
      description: "Simply take photos of medical reports or device readings. AI organizes paper data into digital trends.",
      icon: "CameraAlt"
    },
    {
      number: "03",
      title: "Smart Guardian",
      description: "AI monitors in real-time, sets smart reminders, and answers your health questions anytime.",
      icon: "Insights"
    }
  ];

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "Do I need to buy extra hardware?",
      answer: "No. You only need a smartphone. Our core features like document recognition and AI chat work with your phone's camera."
    },
    {
      question: "How accurate is the medical report recognition?",
      answer: "Our medical OCR models are deeply optimized with over 98% accuracy for key health metrics. For safety, we always prompt you to verify the results."
    },
    {
      question: "How is my data privacy protected?",
      answer: "We use medical-grade AES-256 encryption. Your data is stored securely and is fully compliant with privacy regulations. You can export or delete your data at any time."
    },
    {
      question: "Can it replace my doctor?",
      answer: "No. AgilisCare AI is a home care assistant designed for data organization and general guidance. All medical decisions must be made by a professional healthcare provider."
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-primary-100 selection:text-primary-900">
      <Header currentPage="english" />

      <main className="pt-16">
        {/* Hero Section - Dynamic Breathing Glow Design */}
        <section className="relative pt-20 pb-32 overflow-hidden">
          {/* Advanced Background System */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            {/* Base Mesh Gradient */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(248,250,252,1)_0%,rgba(255,255,255,1)_100%)]" />
            
            {/* Animated Glow Blobs - "Dawn Guardian" Palette */}
            <div className="absolute top-[-10%] right-[-10%] w-[70%] h-[70%] rounded-full bg-sky-300/30 blur-[140px] animate-pulse" style={{ animationDuration: '8s' }} />
            <div className="absolute bottom-[-15%] left-[-10%] w-[60%] h-[60%] rounded-full bg-rose-200/40 blur-[120px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
            <div className="absolute top-[20%] left-[5%] w-[50%] h-[50%] rounded-full bg-amber-200/30 blur-[100px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '4s' }} />
            <div className="absolute bottom-[20%] right-[10%] w-[40%] h-[40%] rounded-full bg-teal-200/20 blur-[90px] animate-pulse" style={{ animationDuration: '15s', animationDelay: '1s' }} />

            {/* Subtle Texture Layer */}
            <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} />
            
            {/* Manual Grid Implementation - Increased opacity */}
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to right, rgba(15, 23, 42, 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.06) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            {/* Decorative Medical/Chemical Icons */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-[15%] left-[10%] opacity-[0.04] rotate-12">
                <Icon iconName="Science" size={120} />
              </div>
              <div className="absolute top-[20%] right-[15%] opacity-[0.03] -rotate-12">
                <Icon iconName="Biotech" size={180} />
              </div>
              <div className="absolute bottom-[25%] left-[15%] opacity-[0.03] rotate-45">
                <Icon iconName="Medication" size={100} />
              </div>
              <div className="absolute bottom-[15%] right-[10%] opacity-[0.04] -rotate-12">
                <Icon iconName="HealthAndSafety" size={140} />
              </div>
              <div className="absolute top-[50%] right-[30%] opacity-[0.02] rotate-90">
                <Icon iconName="Hub" size={200} />
              </div>
            </div>
            
            {/* Bottom Fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 border border-primary-100 mb-8 animate-fadeIn">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
                <span className="text-sm font-semibold text-primary-700 tracking-wide uppercase">AI-Powered Home Care Guardian</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tight text-slate-900 mb-8 max-w-4xl leading-[1.1]">
                Master Your Recovery with <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">Smart</span> Care
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 max-w-2xl mb-12 leading-relaxed">
                Empowering chronic patients and recovering individuals with intelligent document scanning and proactive health monitoring.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-4 animate-fadeIn">
                <Link
                  to="/register"
                  className="w-full sm:w-auto px-10 py-5 bg-slate-900 text-white text-lg font-bold rounded-2xl hover:bg-primary-600 hover:scale-105 transition-all duration-300 shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
                >
                  Start Your Care Journey
                </Link>
                <Link
                  to="#features"
                  className="w-full sm:w-auto px-10 py-5 bg-white text-slate-700 text-lg font-bold rounded-2xl border border-slate-200 hover:bg-slate-50 transition-all duration-300"
                >
                  Explore Features
                </Link>
              </div>

              <div className="mt-20 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all">
                <div className="flex items-center gap-2">
                  <Icon iconName="VerifiedUser" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">MEDICAL GRADE SECURITY</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon iconName="Security" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">100% PRIVACY CONTROL</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon iconName="Speed" size={24} className="text-slate-900" />
                  <span className="font-bold tracking-tighter text-slate-900">INSTANT SCANNING</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stories Section */}
        <section className="py-24 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <div className="max-w-2xl">
                <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Real Life Stories</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-slate-900">Caring in Every Moment. <br />Quietly Guarding with AI.</h3>
              </div>
              <p className="text-lg text-slate-500 max-w-sm">
                We turn complex technology into simple, heartfelt care tailored for your specific needs.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {patientPersonas.map((persona) => (
                <div key={persona.id} className="group flex flex-col bg-white rounded-[2.5rem] p-10 border border-slate-100 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.08)] transition-all duration-500">
                  <div className={`w-16 h-16 rounded-2xl bg-${persona.color}-50 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                    <Icon iconName={persona.icon} size={32} className={`text-${persona.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-bold text-slate-900">{persona.name}</span>
                      <span className="px-3 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider">{persona.title}</span>
                    </div>
                    <p className="text-slate-400 font-medium mb-8">{persona.condition} · Age {persona.age}</p>
                    
                    <div className="space-y-6">
                      <div className="relative pl-6 border-l-2 border-red-100">
                        <span className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                        </span>
                        <p className="text-slate-600 text-sm leading-relaxed">{persona.challenge}</p>
                      </div>
                      <div className="relative pl-6 border-l-2 border-primary-100">
                        <span className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary-50 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                        </span>
                        <p className="text-slate-900 text-sm font-bold leading-relaxed">{persona.solution}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-10 pt-8 border-t border-slate-50 flex items-center justify-between">
                    <span className="text-sm font-bold text-emerald-600">Benefit: {persona.benefit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <section id="features" className="py-24 bg-white relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-24">
              <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Capabilities</h2>
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Master Your Health, Simply.</h3>
              <p className="text-xl text-slate-500 max-w-2xl mx-auto">Expert health management without needing a medical degree.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {coreFeatures.map((feature) => (
                <div key={feature.id} className="relative group">
                  <div className={`absolute -inset-4 rounded-[3rem] bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                  <div className="relative">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg mb-8`}>
                      <Icon iconName={feature.icon} size={32} />
                    </div>
                    <h4 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h4>
                    <div className="inline-block px-3 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-md mb-6">
                      {feature.benefit}
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-8">{feature.description}</p>
                    <ul className="space-y-4">
                      {feature.features.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Icon iconName="CheckCircle" size={20} className="text-emerald-500 mt-0.5 shrink-0" />
                          <span className="text-slate-700 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demo */}
        <section className="py-24 bg-slate-900 overflow-hidden relative">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:40px_40px]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex flex-col lg:flex-row items-center gap-20">
              <div className="lg:w-5/12">
                <h2 className="text-sm font-black text-primary-400 uppercase tracking-[0.3em] mb-4">Interactive Demo</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-8">Your AI Assistant, <br />Anytime, Anywhere.</h3>
                <div className="flex flex-col gap-4">
                  {useCases.map((useCase, index) => (
                    <button
                      key={useCase.id}
                      onClick={() => setActiveUseCase(index)}
                      className={`flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 text-left ${
                        activeUseCase === index 
                        ? 'bg-white text-slate-900 shadow-[0_20px_40px_rgba(0,0,0,0.3)] scale-105' 
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeUseCase === index ? 'bg-primary-100 text-primary-600' : 'bg-white/10 text-white'}`}>
                        <Icon iconName={useCase.icon} size={24} />
                      </div>
                      <span className="text-lg font-bold">{useCase.title}</span>
                      {activeUseCase === index && <Icon iconName="ChevronRight" size={20} className="ml-auto" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lg:w-7/12 w-full">
                <div className="bg-slate-800 rounded-[2.5rem] border border-slate-700 shadow-2xl overflow-hidden">
                  <div className="bg-slate-700/50 p-6 flex items-center border-b border-slate-700">
                    <div className="flex gap-1.5 mr-6">
                      <div className="w-3 h-3 rounded-full bg-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-amber-500/50" />
                      <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                        <Icon iconName="SmartToy" size={18} className="text-white" />
                      </div>
                      <span className="text-white font-bold tracking-tight">AgilisCare AI Health Bot</span>
                    </div>
                  </div>
                  
                  <div className="p-8 space-y-8 min-h-[500px] flex flex-col justify-end">
                    {useCases[activeUseCase].messages.map((message, i) => (
                      <div key={i} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-slideIn`}>
                        <div className={`max-w-[85%] rounded-[2rem] p-6 ${
                          message.type === 'user' 
                          ? 'bg-primary-600 text-white rounded-br-none' 
                          : 'bg-slate-700 text-slate-100 rounded-bl-none'
                        }`}>
                          <p className="text-base leading-relaxed whitespace-pre-line">{message.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-6 bg-slate-900/50 border-t border-slate-700 flex gap-4">
                    <div className="flex-1 bg-slate-800 rounded-xl px-6 py-3 border border-slate-700 text-slate-500 italic flex items-center">
                      AI is analyzing your request...
                    </div>
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center text-white">
                      <Icon iconName="Send" size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="md:w-1/3">
                <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em] mb-4">Onboarding</h2>
                <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">Start Your Care <br />In 3 Steps.</h3>
                <Link to="/register" className="inline-flex items-center text-primary-600 font-bold hover:gap-4 transition-all group">
                  Join Now <Icon iconName="ArrowForward" size={24} className="ml-2 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-3 gap-12">
                {steps.map((step) => (
                  <div key={step.number} className="group">
                    <div className="text-8xl font-black text-slate-100 mb-[-3rem] group-hover:text-primary-50 transition-colors duration-500 select-none">
                      {step.number}
                    </div>
                    <div className="relative pt-8">
                      <h4 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h4>
                      <p className="text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 bg-slate-50/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-sm font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Help Center</h2>
              <h3 className="text-4xl font-bold text-slate-900">Common Questions</h3>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-8 py-6 text-left flex justify-between items-center group"
                  >
                    <span className="font-bold text-slate-800 text-lg group-hover:text-primary-600 transition-colors">
                      {faq.question}
                    </span>
                    <Icon
                      iconName="ExpandMore"
                      size={24}
                      className={`text-slate-400 transition-transform duration-300 ${expandedFaq === index ? 'rotate-180 text-primary-600' : ''}`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-8 pb-8 text-slate-600 leading-relaxed animate-fadeIn">
                      <p className="pt-2 border-t border-slate-50">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-white px-4">
          <div className="max-w-7xl mx-auto">
            <div className="relative bg-slate-900 rounded-[3rem] p-12 lg:p-24 overflow-hidden text-center">
              <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]" />
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-600/20 via-transparent to-indigo-600/20" />
              
              <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tight">
                  Caring Technology for Every Home.
                </h2>
                <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
                  Join thousands who trust AgilisCare AI for their health management.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link
                    to="/register"
                    className="px-10 py-5 bg-white text-slate-900 text-xl font-bold rounded-2xl hover:scale-105 transition-all shadow-[0_20px_50px_rgba(255,255,255,0.1)]"
                  >
                    Sign Up for Free
                  </Link>
                  <Link
                    to="/"
                    className="px-10 py-5 bg-slate-800 text-white text-xl font-bold rounded-2xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    View Professional Mode
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomeCarePage;
